import { useTranslation } from 'react-i18next';
import React, { useState, useRef } from 'react';
import { Layers } from 'lucide-react';
import { useMapStore, BasemapType } from '../../store/map.store';
import { useMap } from '../../hooks/useMap';
import { cn } from '../../utils/cn';

interface MapTypeOption {
  id: BasemapType;
  label: string;
}

export const MapTypeSelector: React.FC = () => {
  const { t } = useTranslation();
  const { basemapType, setBasemap } = useMapStore();
  const { setBasemap: setBasemapHook } = useMap();
  const [expanded, setExpanded] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const mapTypes: MapTypeOption[] = [
    { id: 'road', label: t('basemap.road') },
    { id: 'satellite', label: t('basemap.satellite') },
    { id: 'hybrid', label: t('basemap.hybrid') },
    { id: 'terrain', label: t('basemap.terrain') },
    { id: 'gray', label: t('basemap.gray') },
  ];

  const handleMapTypeChange = (type: BasemapType) => {
    setBasemap(type);
    setBasemapHook(type);
  };

  const handleMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setExpanded(true);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setExpanded(false);
    }, 300);
  };

  const selectedLabel = mapTypes.find((t) => t.id === basemapType)?.label || t('basemap.road');

  return (
    <div
      className="fixed bottom-6 right-6 z-40"
      role="group"
      aria-label={t('basemap.selector')}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="flex items-center">
        {/* Expanded options — slide out to the left */}
        <div
          className={cn(
            'flex items-center gap-1 overflow-hidden',
            'transition-all duration-300 ease-out origin-right',
            expanded
              ? 'max-w-[500px] opacity-100 mr-1'
              : 'max-w-0 opacity-0 mr-0',
          )}
        >
          <div className="bg-gn-surface backdrop-blur-sm rounded-lg p-1 shadow-lg border border-gn-light flex gap-1 whitespace-nowrap">
            {mapTypes.map((mapType) => (
              <button
                key={mapType.id}
                onClick={() => handleMapTypeChange(mapType.id)}
                className={cn(
                  'px-3 py-1.5 rounded-md text-xs font-medium',
                  'transition-all duration-200',
                  basemapType === mapType.id
                    ? 'bg-gn-secondary text-gn-primary shadow-sm'
                    : 'bg-transparent text-gn-muted hover:text-gn-primary hover:bg-gn-tertiary',
                  'focus:outline-none focus:ring-2 focus:ring-gray-400',
                )}
                aria-pressed={basemapType === mapType.id}
              >
                {mapType.label}
              </button>
            ))}
          </div>
        </div>

        {/* Collapsed icon button — always visible */}
        <button
          onClick={() => setExpanded(!expanded)}
          className={cn(
            'flex items-center gap-1.5',
            'px-3 py-2 rounded-lg shadow-lg',
            'bg-gn-surface backdrop-blur-sm border border-gn-light',
            'text-gn-muted hover:text-gn-primary hover:bg-gn-tertiary',
            'transition-all duration-200',
            'focus:outline-none focus:ring-2 focus:ring-gray-400',
          )}
          title={t('basemap.current', { name: selectedLabel })}
          aria-label={t('basemap.toggle')}
        >
          <Layers size={16} />
          <span className="text-xs font-medium">{selectedLabel}</span>
        </button>
      </div>
    </div>
  );
};
